/* eCO Filing Helper admin panel — pick a book, view the pre-filled
 * clipboard bundle, download the deposit PDF, deep-link to eco.copyright.gov.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, ClipboardCopy, ExternalLink, Loader2, Download } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function EcoFilingPanel() {
  const [books, setBooks] = useState([]);
  const [bookId, setBookId] = useState('');
  const [prep, setPrep] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get('/admin/bookstore/books').then((r) => setBooks(r.data?.books || []));
  }, []);

  const load = async (bid) => {
    if (!bid) return;
    setBusy(true);
    try {
      const r = await http.get(`/admin/eco/book/${bid}/prep`);
      setPrep(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Prep failed.'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(bookId); }, [bookId]);

  const copyClipboard = () => {
    if (!prep?.clipboard) return;
    navigator.clipboard.writeText(prep.clipboard);
    toast.success('Bundle copied — paste into the eCO Standard Application.');
  };

  return (
    <div className="space-y-4" data-testid="admin-eco-panel">
      <div>
        <h2 className="flex items-center gap-2 text-[16px] font-semibold tracking-tight">
          <FileText className="h-4 w-4 text-primary" /> eCO Filing Prep
        </h2>
        <p className="mt-0.5 text-[11.5px] text-muted-foreground">
          One-tap package of the fields the U.S. Copyright Office eCO Standard
          Application asks for, plus a downloadable deposit PDF.
        </p>
      </div>

      <label className="block max-w-md">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Book</div>
        <select
          className="mt-1 h-8 w-full rounded-sm border border-border bg-background px-2 text-[12.5px]"
          value={bookId}
          data-testid="eco-book-select"
          onChange={(e) => setBookId(e.target.value)}
        >
          <option value="">—</option>
          {books.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}
        </select>
      </label>

      {busy && (
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {prep && (
        <div className="grid gap-4 md:grid-cols-2">
          <section className="rounded-md border border-border bg-card p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Clipboard bundle</div>
              <button
                type="button" onClick={copyClipboard}
                data-testid="eco-copy-clipboard"
                className="inline-flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-1 text-[10.5px] hover:bg-secondary"
              >
                <ClipboardCopy className="h-3 w-3" /> Copy
              </button>
            </div>
            <pre className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-sm border border-border bg-panel-header/40 p-2 text-[11px] font-mono">
{prep.clipboard}
            </pre>
          </section>

          <section className="rounded-md border border-border bg-card p-3">
            <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">Deposit PDF + links</div>
            <a
              href={`${API}/admin/eco/book/${bookId}/deposit.pdf`}
              target="_blank" rel="noreferrer"
              data-testid="eco-download-deposit"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90"
            >
              <Download className="h-4 w-4" /> Download deposit PDF
            </a>
            <div className="mt-3 space-y-1.5 text-[12px]">
              {Object.entries(prep.deep_links || {}).map(([k, v]) => (
                <a key={k} href={v} target="_blank" rel="noreferrer"
                   data-testid={`eco-link-${k}`}
                   className="inline-flex items-center gap-1 text-primary underline">
                  <ExternalLink className="h-3 w-3" /> {k.replace(/_/g, ' ')}
                </a>
              )).reduce((acc, el) => [acc, <br key={Math.random()} />, el])}
            </div>
            {prep.notes?.length > 0 && (
              <ul className="mt-3 list-disc pl-4 text-[11px] text-muted-foreground">
                {prep.notes.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
